import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { ScrollTopButton } from "./components/ScrollTopButton";
import type { AlertConfig } from "../core/alerts";
import { activeLeadMinutes, DEFAULT_ALERT_CONFIG, DEFAULT_LEADS, dueSpokenReminder, isWithinActiveWindow, isWithinHomeWindow, isWithinRideHomeWindow } from "../core/alerts";
import { adminLoginForDevice } from "../core/admin-logins";
import type { CommutePlan, CommuteRequest, RiderPhase, SimulationOverrides } from "../shared/types";
import { distanceMeters, gtfsWeekdayIndex, serviceDateKey } from "../shared/time";
import { stationLabel } from "../shared/labels";
import { defaultOriginFor, homeAt, homeForStation, inTransbayTube, ORIGIN_PRESETS, simulatedLocationLabel, stationForHome } from "../shared/origins";
import {
	ARRIVAL_METERS,
	boardingRadiusFor,
	destinationEvidence,
	FINAL_PLACE_METERS,
	INITIAL_JOURNEY_STATE,
	nextJourneyState,
	nextPinnedTrainId,
	observedAtDestination,
	observedLeavingDestination,
	type DestinationEvidence,
	type JourneySignals,
} from "../core/journey";
import { observedOnboardStatus } from "../core/onboard-status";
import { boardingGeofenceCoordinates } from "../core/drop-off";
import { COLLEGE_PREP_BOUNDARY, DESTINATION_ROLES, morningProfileForStation, profileById } from "../core/profiles";
import { isPlatformSighting, latchBoardedTrain } from "../core/aboard";
import { pinChange, type PinOrigin } from "../core/pin-origin";
import { cardQuestionFor, isBefore, isBoarded, isCommitted, phasesForJourney, spokenRemindersFor } from "../core/rider-journey";
import { MAP_STATIONS } from "./bart-network";
import { report } from "./report";
import { RoutePicker, SimulationBanner, StatusBar } from "./components/Chrome";
import { AlertsSheet } from "./components/Alerts";
import { noteGesture } from "./voice";
import { playChime, unlockAudio, type ChimeTier } from "./chime";
import { cancelPendingSpeech, prepareSpeech, speak } from "./speech";
import type { SpeechVariant } from "../shared/speech-catalogue";
import { noteAlertDiagnostics, noteFooterGap } from "./alertDiagnostics";
import { AlertSheet, ServiceHealthCard, SupportingMetrics, TrainBoard, UpstreamTrace } from "./components/Details";
import { PlanDeck, OnwardCard } from "./components/PlanDeck";
import { wazeNavigationUrl, type NavTarget } from "./navigation";
import { PickupBlock, PickupButton, PickupCard } from "./components/Pickup";
import { PickupCommunication } from "./components/PickupCommunication";
import { PickupPlanning, PickupPlanningButton } from "./components/PickupPlanning";
import { PICKUP_PLANNING_UNTIL_MINUTES, pickupActivityWithinHorizon, pickupCandidates, pickupPlanningCutoffMs, pickupTiming, pickupTimingFor } from "../core/pickup";
import { usePickup } from "./usePickup";
import { useFollowStatus } from "./useFollowStatus";
import type { PickupReport } from "../shared/types";
import type { OnwardLeg } from "../core/format";
import { ProgressLine } from "./components/ProgressLine";
import { DebugPanel, SettingsSheet } from "./components/Settings";
import { GuideSheet } from "./components/Guide";
import { PickupMaps } from "./components/PickupMaps";
import { planningMapFix } from "../core/pickup-map";
import { useMapLocationDemand } from "./useMapLocation";
import { WhatsNew } from "./components/WhatsNew";
import { RiderMonitor } from "./components/RiderMonitor";
import { pickupCalledOff } from "../core/monitor";
import { estimatedFollowMapPosition, followJourneyOverlay, followMapPosition, projectFollowStatus, reconcileFollowWithPickup, showMorningFollowMap } from "../core/follow";
import { NextStepButton } from "./components/NextStepButton";
import { useVersionWatch } from "./useVersionWatch";
import { useGeolocation, type StationRecord } from "./useGeolocation";
import { shouldRecordPhase } from "../core/station-telemetry";
import { planDiagnostic, useJourneyDiagnostics } from "./useJourneyDiagnostics";
import { useWakeLock } from "./useWakeLock";
import { useJourneyPin, useNativeJourney, useNativeLocation, useNativeWatch } from "./useNativeShell";
import { useTravelSchedule } from "./useTravelSchedule";
import { useLocationOverride } from "./useLocationOverride";
import { useMapTest } from "./useMapTest";
import { DEFAULT_TRAVEL_DAYS, planningMorning, scheduledStation } from "../core/travel-schedule";
import { journeyFinished, nativeJourneyFrom, nativeLocationActive, nativePickupPresentation } from "../core/native-journey";
import { postHaptic, postIdentity, setHapticIntensity, shellDeviceId, shellVersion } from "./native";
import { NATIVE_BRIDGE_VERSION, type NativeActionReport, type NativePinRequest } from "../shared/native-bridge";
import { getDeviceId } from "./useDeviceId";
import { useNow, useSnapshot } from "./useSnapshot";
import { useJourneyProgress } from "./useJourneyProgress";
import { usePinnedTrain } from "./usePinnedTrain";
import { useExtraTrains } from "./useExtraTrains";
import { useDirection } from "./useDirection";
import { useLastSeen } from "./lastSeen";
import { BUILD_ID } from "./build";
import { adoptedAlertDefaults, usePreferences } from "./usePreferences";

/** The ride home has no deadline and no drive, so it is a direction rather than a profile choice. */
const HOME_PROFILE_ID = "home-pm";

/** How long before the first reminder the screen starts being held awake. */
const WAKE_LEAD_MS = 10 * 60_000;
/** The same out-the-door allowance used by both Pickup card variants. */
const PICKUP_PREP_SECONDS = 5 * 60;

/** Close enough to a station to be worth recording as "he was here". */
const STATION_PASS_METERS = 300;

/** How far the planner's frozen origin may lag the driver before the drive is re-measured. */
const PLANNING_ORIGIN_DRIFT_METERS = 500;

/**
 * The one-tap declare.
 *
 * Held rather than tapped now, and a backward step is refused for two seconds after a forward one
 * (`DECLARE_SETTLE_MS`). Between them those close the hole this was switched off for on
 * 2026-09-01: the button is laid over the progress line and unmounts on commit, so the press that
 * followed it landed on the line underneath and walked the phase back.
 */
const SHOW_NEXT_STEP_BUTTON: boolean = true;

/**
 * How the band behind the bottom action reads.
 *
 * `haze` is the progressive blur that shipped: four masked layers fading the page out as it passes
 * under the button. `glass` drops the band entirely and lets the pill blur its own patch instead.
 * Both are kept so the comparison can be made on a moving train rather than at a desk — flip this
 * one value.
 */
const ACTION_BAND: "haze" | "glass" = "glass";

/**
 * The drive between a house and its station, which is the last leg of the way home.
 *
 * The same figures the morning is planned against, used backwards. Quoted on the card as an
 * estimate, because that is what they are.
 */
const HOME_DRIVE_SECONDS: Record<string, number> = { richmond: 20 * 60, "twin-peaks": 10 * 60 };

export function App() {
	const [preferences, setPreferences] = usePreferences();
	useLastSeen();
	// Restored as he left it while the session is still live; the clock and where he is standing
	// only get to speak once the app has been shut for long enough that the leg is over.
	const [direction, { choose: chooseDirection, infer: inferDirection }] = useDirection();
	// Null means "use my location", which is always the starting state: the phone knows where it
	// is far more reliably than a setting remembered from yesterday.
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [guideOpen, setGuideOpen] = useState(false);
	const [noticesOpen, setNoticesOpen] = useState(false);
	const [pickupPlanningOpen, setPickupPlanningOpen] = useState(false);
	const [pickupPlanningOrigin, setPickupPlanningOrigin] = useState<{ lat: number; lon: number } | null>(null);
	const [pickupPlanningSelectedId, setPickupPlanningSelectedId] = useState<string | null>(null);
	const [reopenActivity, setReopenActivity] = useState(false);
	useVersionWatch();
	const [alertsOpen, setAlertsOpen] = useState(false);
	const [alertsEnabled, setAlertsEnabled] = useState(false);
	/** True when someone follows this phone, which is the app's record of who is the rider. */
	const [followed, setFollowed] = useState(false);
	// The reminders the push alerts use, so the spoken ones keep the same schedule.
	const [alertLeads, setAlertLeads] = useState<number[]>(() => activeLeadMinutes({ ...DEFAULT_ALERT_CONFIG, leads: DEFAULT_LEADS }));
	// Set when an alert is saved, cleared once a snapshot built from those settings has landed.
	const [replanSince, setReplanSince] = useState<number | null>(null);
	// Held for the status-bar tap the shell forwards: iOS aims that at the web view, not at the
	// element the page actually scrolls.
	const scrollRef = useRef<HTMLElement | null>(null);
	const deviceId = useMemo(() => getDeviceId(), []);
	const isDavid = adminLoginForDevice(deviceId) === "david";

	const nowMs = useNow();
	const mapTest = useMapTest(deviceId, settingsOpen, nowMs);

	const travelSchedule = useTravelSchedule(deviceId);
	const travelDays = travelSchedule.data?.days ?? DEFAULT_TRAVEL_DAYS;
	const scheduledMorning = scheduledStation(travelDays, nowMs, "school");
	const scheduledReturn = scheduledStation(travelDays, nowMs, "home");
	// A failed refresh does not invalidate the last schedule or the active journey.
	const scheduledTravel = !!travelSchedule.data && (direction === "school" ? scheduledMorning : scheduledReturn) !== null;
	const scheduledPickupTravel = !!travelSchedule.data && scheduledReturn !== null;
	const scheduledDays = travelDays.flatMap((day, index) => day.am === null ? [] : [index]);
	const todaySchoolProfileId = profileFromUrl() ?? morningProfileForStation(scheduledMorning)?.id ?? preferences.defaultProfileId ?? "civic-am";

	// URL overrides win, so a scenario can be shared as a link without touching stored settings.
	const [urlSimulation, setUrlSimulation] = useState(() => simulationFromUrl());
	// Standing somewhere by hand, for trying a morning that starts at the other house.
	const simulatedAt = urlSimulation?.position ?? preferences.simulation?.position ?? null;
	const locationOverride = useLocationOverride(deviceId, simulatedAt);

	// The morning is judged on the rider's own clock, because the phase has to be known before the
	// snapshot that would otherwise supply the agency's.
	const localTimezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
	const targetMinutes = preferences.targetMinutes ?? DEFAULT_ALERT_CONFIG.targetMinutes;
	const withinWindow = scheduledTravel && isWithinActiveWindow(nowMs, localTimezone, targetMinutes);
	const withinHomeWindow = scheduledTravel && isWithinHomeWindow(nowMs, localTimezone, targetMinutes);
	const withinRideHomeWindow = isWithinRideHomeWindow(nowMs, localTimezone);

	/*
	 * Watch the phone whenever the journey has a use for it, and never otherwise — the watch is
	 * what raises the permission prompt, so it should not start on a morning nobody is having.
	 *
	 * From the moment the morning is live, because from there the plan is measured from where he
	 * actually is. The old gate was "alerts have ever been on", which both ran all day and meant
	 * saying "I am driving" started nothing: with no fix the plan fell back to the commute's own
	 * address and reported the drive from a house he had already left. A render behind, because
	 * whether the journey wants a position is itself decided from one.
	 */
	const wantsPositionRef = useRef(false);
	// Pickup mode has its own claim on the watch: David's own morning is over, so nothing else
	// would be asking for a fix, and his position is the whole of what Eben is shown. A render
	// behind for the same reason as the one above.
	const pickupWantsPositionRef = useRef(false);
	// Only the rider's phone keeps the detailed record, and only on a leg that ends at a platform.
	// A render behind for the same reason as the two above: the phase is worked out from a fix.
	const stationTelemetryRef = useRef<StationRecord | null>(null);
	// Assigned during render further down, once the journey controls a tap needs actually exist.
	// The shell is the only caller and it calls from an event, never while rendering.
	const nativeActionRef = useRef<(report: NativeActionReport) => void>(() => {});
	// Same reason: the watch names a train, and the pin cannot be moved until there is a deck to move it in.
	const nativePinRef = useRef<(pin: NativePinRequest) => void>(() => {});
	const nativeShell = useNativeLocation(
		(tapped) => nativeActionRef.current(tapped),
		(pin) => nativePinRef.current(pin),
	);
	const supportsActivityPurposes = nativeShell.active && (shellVersion() ?? 0) >= NATIVE_BRIDGE_VERSION;
	// Only on a first run: after the shell has adopted an id it is the one being read back here, and
	// the shell ignores a second. Without it the shell has nothing to post fixes as while we sleep.
	useEffect(() => {
		if (nativeShell.active && !shellDeviceId()) {
			postIdentity(deviceId);
		}
	}, [nativeShell.active, deviceId]);
	/*
	 * Keep the server's copy of the push token honest.
	 *
	 * Apple reissues tokens — after a restore, an OS update, or a build signed with a different
	 * entitlement, which is exactly what moving from a sideload to TestFlight is. Until this, the
	 * token only reached the server when he saved his alerts, so a reissued one meant a phone that
	 * quietly stopped being alerted with nothing anywhere reporting a problem.
	 *
	 * Cheap and idempotent: the server compares before writing and most launches change nothing.
	 */
	useEffect(() => {
		const push = nativeShell.push;
		if (!push) {
			return;
		}
		void fetch("/api/alerts/token", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ deviceId, token: push.token, environment: push.environment }),
		}).catch(() => {
			// The next launch tries again, and nothing on this screen depends on it.
		});
	}, [nativeShell.push, deviceId]);
	// Set once here rather than passed to every caller: a buzz is fired from components that have
	// no business reading preferences.
	setHapticIntensity(preferences.hapticIntensity ?? 1);
	const mapLocationWanted = useMapLocationDemand();
	// Arm location with the same window that moves the journey to at_home, without a render lag.
	const geolocation = useGeolocation(
		withinHomeWindow || pickupPlanningOpen || wantsPositionRef.current || pickupWantsPositionRef.current || mapLocationWanted,
		defaultOriginFor(direction === "home" ? HOME_PROFILE_ID : todaySchoolProfileId),
		simulatedAt,
		stationTelemetryRef.current,
		nativeShell,
		mapLocationWanted,
	);
	const openPickupPlanning = useCallback(() => {
		setPickupPlanningOrigin(geolocation.position ? { lat: geolocation.position.lat, lon: geolocation.position.lon } : null);
		setPickupPlanningSelectedId(null);
		setReopenActivity(supportsActivityPurposes);
		setPickupPlanningOpen(true);
	}, [geolocation.position, supportsActivityPurposes]);
	const closePickupPlanning = useCallback(() => {
		setReopenActivity(supportsActivityPurposes);
		setPickupPlanningOpen(false);
		setPickupPlanningOrigin(null);
		setPickupPlanningSelectedId(null);
	}, [supportsActivityPurposes]);
	/*
	 * The origin the planner prices its drive from.
	 *
	 * Held steady rather than tracked, so a car in motion does not turn every GPS fix into a
	 * routing request — the 15-second poll still refreshes traffic from the same place. But a
	 * driver who has genuinely moved has to be re-measured, or "leave in 5 minutes" quietly goes
	 * on describing where he was when he opened it.
	 */
	useEffect(() => {
		if (!pickupPlanningOpen || !geolocation.position) {
			return;
		}
		const here = { lat: geolocation.position.lat, lon: geolocation.position.lon };
		if (pickupPlanningOrigin == null || distanceMeters(here, pickupPlanningOrigin) > PLANNING_ORIGIN_DRIFT_METERS) {
			setPickupPlanningOrigin(here);
		}
	}, [pickupPlanningOpen, pickupPlanningOrigin, geolocation.position]);
	// The ride home is its own journey: no drive, no deadline, no house to leave. What it shares
	// with the school run is the half that starts at a station.
	const hasAccessLeg = direction !== "home";
	const [journeyState, setJourneyState] = useState(INITIAL_JOURNEY_STATE);
	const [journeyStateDirection, setJourneyStateDirection] = useState(direction);
	if (journeyStateDirection !== direction) {
		setJourneyStateDirection(direction);
		setJourneyState(INITIAL_JOURNEY_STATE);
	}
	const { awayFromHome: driving } = geolocation;
	const [journeyProgress, journey] = useJourneyProgress(
		direction,
		hasAccessLeg,
		{
			withinWindow: withinHomeWindow,
			awayFromHome: driving,
			atStation: journeyState.atStation,
			atDestination: journeyState.atDestination,
			arrived: journeyState.arrived,
			inTube: inTransbayTube(geolocation.position),
		},
		nowMs,
		scheduledTravel,
	);
	const plannedMorning = planningMorning(travelDays, nowMs, targetMinutes, preferences.lateToleranceMinutes ?? profileById(todaySchoolProfileId)?.target?.lateToleranceMinutes ?? 0);
	const schoolStation = journeyProgress.phase === "planning" ? plannedMorning?.station ?? scheduledMorning : scheduledMorning;
	const schoolProfileId = profileFromUrl() ?? morningProfileForStation(schoolStation)?.id ?? todaySchoolProfileId;
	const activeProfileId = direction === "home" ? HOME_PROFILE_ID : schoolProfileId;
	const commuteOrigin = defaultOriginFor(activeProfileId);
	const journeyPhases = useMemo(() => phasesForJourney(hasAccessLeg), [hasAccessLeg]);
	const resetJourney = journey.reset;
	// One phase decides all of it: which question the card answers, which reminders may still be
	// spoken, and whether the chosen train has stopped following the recommendation.
	const cardMode = cardQuestionFor(journeyProgress.phase);
	// "Show more trains": sticky for today, but only for this direction and this view — reaching
	// the platform is a different question than standing at school was, and an old tap must not
	// silently answer a question he has not asked yet.
	const [extraTrains, requestMoreTrains] = useExtraTrains(direction, cardMode);
	const { leave: canSpeakLeave, train: canSpeakTrain } = spokenRemindersFor(journeyProgress.phase);
	const committed = isCommitted(journeyProgress.phase);
	const boarded = isBoarded(journeyProgress.phase);
	/*
	 * Where the plan starts from.
	 *
	 * Planning has no location: the commute names its own starting point, and it can be thought
	 * about from anywhere. From the moment the morning is live, the question becomes how he gets
	 * there from where he actually is.
	 */
	const planFromHere = hasAccessLeg && !isBefore(journeyProgress.phase, "at_home");
	/*
	 * Wanted for the whole journey now, not just the drive.
	 *
	 * Rockridge and College Prep are both noticed from a fix, and the ones in between — including
	 * the ones underground that come back useless — are the only record of where he actually was
	 * while the app was deciding things. Planning is the exception: a morning being thought about
	 * is not one being had, and asking for location then is asking for nothing.
	 */
	wantsPositionRef.current = !isBefore(journeyProgress.phase, "at_home") && journeyProgress.phase !== "arrived";

	/*
	 * The steps the phone cannot see.
	 *
	 * Reaching a platform is declared because nothing observable marks it, and the ride home's walk
	 * out of school has no phase at all. Recording both in full is how that stays a judgement about
	 * evidence rather than a guess — and it is kept to the phone the morning is actually about,
	 * which is the one someone follows.
	 */
	stationTelemetryRef.current =
		followed && shouldRecordPhase(journeyProgress.phase, hasAccessLeg)
			? { phase: journeyProgress.phase, leg: hasAccessLeg ? "school" : "home" }
			: null;

	// Sampled rather than followed: every fix would mean a routing call every few seconds.
	const [livePosition, setLivePosition] = useState<{ lat: number; lon: number } | null>(null);
	const livePositionAtRef = useRef(0);

	/*
	 * One way in, so every pin change can say who made it.
	 *
	 * The surrounding facts come from a ref because this is called from effects declared further
	 * down the file: closing over them would report the state of the render that defined it, and a
	 * stale `fromId` would misreport the very thing being watched.
	 */
	const pinContextRef = useRef<{ pinnedId: string | null; recommendedId: string | null; guessedId: string | null; boarded: boolean }>({
		pinnedId: null,
		recommendedId: null,
		guessedId: null,
		boarded: false,
	});
	const destinationEvidenceRef = useRef<DestinationEvidence | null>(null);
	const followPlanContextRef = useRef<{ plans: CommutePlan[]; profileId: string; direction: string; destinationIndex: number; boardingStationName: string | null } | null>(null);
	const applyPin = useCallback((id: string | null, origin: PinOrigin) => {
		const context = pinContextRef.current;
		setSelectedId(id);
		setPinnedId(id);
		const change = pinChange({
			origin,
			fromId: context.pinnedId,
			toId: id,
			recommendedId: context.recommendedId,
			guessedId: context.guessedId,
			boarded: context.boarded,
		});
		// The repair effect re-runs on every snapshot while the pin is empty; a row a minute saying
		// nothing moved would bury the ones where something did.
		if (!change.changed && !change.byUser) {
			return;
		}
		const followContext = followPlanContextRef.current;
		const plan = followContext?.plans.find(candidate => candidate.id === id) ?? null;
		report("train-selection", {
			...change, planId: id, visibility: document.visibilityState,
			direction: followContext?.direction ?? null,
			profileId: followContext?.profileId ?? null,
			boardingStationName: followContext?.boardingStationName ?? null,
			destinationStopId: plan?.destinations[followContext?.destinationIndex ?? 0]?.stopId ?? plan?.destinations[0]?.stopId ?? null,
			plan: planDiagnostic(plan),
		});
	}, []);
	// Declared here so it runs before the effects that move the pin on the same commit.
	useEffect(() => {
		pinContextRef.current = {
			pinnedId,
			recommendedId: snapshot?.recommendedId ?? null,
			guessedId: guessedTrainId,
			boarded,
		};
	});

	/*
	 * Standing somewhere else by hand invalidates everything that was measured from where he was.
	 *
	 * Only on the way out, though. Moving the pin along a journey is the journey being tested, so
	 * it carries the phase with it; handing the app back to the real phone is what starts over.
	 */
	const simulatedKey = simulatedAt ? `${simulatedAt.lat},${simulatedAt.lon}` : null;
	const simulationRef = useRef(simulatedKey);
	useEffect(() => {
		if (simulationRef.current === simulatedKey) {
			return;
		}
		simulationRef.current = simulatedKey;
		livePositionAtRef.current = 0;
		setLivePosition(null);
		if (simulatedKey != null) {
			return;
		}
		setJourneyState(INITIAL_JOURNEY_STATE);
		applyPin(null, "reset");
		destinationEvidenceRef.current = null;
		resetJourney();
	}, [simulatedKey, resetJourney, applyPin]);
	// A latch belongs to one leg: an arrival on the way home is not one on the way to school.
	const directionRef = useRef(direction);
	useEffect(() => {
		if (directionRef.current === direction) {
			return;
		}
		directionRef.current = direction;
		destinationEvidenceRef.current = null;
	}, [direction]);

	/*
	 * Standing in one of the houses means the next journey starts there.
	 *
	 * The clock used to decide this alone, so opening the app at home after dinner offered the
	 * ride home from a station he was nowhere near. Where he is beats what time it is, and only
	 * until he says otherwise.
	 */
	const houseId = homeAt(geolocation.position)?.id ?? null;
	useEffect(() => {
		if (houseId != null) {
			inferDirection("school");
		}
	}, [houseId, inferDirection]);

	useEffect(() => {
		if (!travelSchedule.data) return;
		const profileId = morningProfileForStation(scheduledMorning)?.id ?? null;
		if (preferences.defaultProfileId !== profileId || preferences.origin !== null) setPreferences({ defaultProfileId: profileId, origin: null });
	}, [scheduledMorning, travelSchedule.data, preferences.defaultProfileId, preferences.origin, setPreferences]);

	useEffect(() => {
		if (!planFromHere || !geolocation.position) {
			setLivePosition(null);
			return;
		}
		setLivePosition({ lat: geolocation.position.lat, lon: geolocation.position.lon });
	}, [planFromHere, geolocation.position]);

	const planOrigin = planFromHere ? livePosition : commuteOrigin;

	/*
	 * Where he goes once the train has done its part.
	 *
	 * Outbound there is one answer; homeward there are two houses and he picks. Read off the
	 * profile rather than the snapshot, because the request that fetches the snapshot is the one
	 * that has to ask for this leg to be routed.
	 */
	const activeProfile = useMemo(() => profileById(activeProfileId), [activeProfileId]);
	const [onwardRouteSessionId, setOnwardRouteSessionId] = useState(() => crypto.randomUUID());
	const corridorStationId = direction === "home" ? scheduledReturn : schoolStation;
	const chooseCorridor = useCallback(
		(stationId: string | null) => {
			if (!travelSchedule.data || travelSchedule.saving || (stationId !== "CIVC" && stationId !== "GLEN" && stationId !== "SFIA")) return;
			const choiceDateMs = direction === "school" && journeyProgress.phase === "planning" ? plannedMorning?.targetMs ?? nowMs : nowMs;
			const weekday = gtfsWeekdayIndex(serviceDateKey(choiceDateMs, "America/Los_Angeles"));
			const period = direction === "home" ? "pm" : "am";
			const days = travelSchedule.data.days.map((day, index) => index === weekday ? { ...day, [period]: stationId } : day);
			void travelSchedule.save(days, travelSchedule.data.version).then(accepted => {
				if (accepted) setOnwardRouteSessionId(crypto.randomUUID());
			});
		},
		[direction, nowMs, travelSchedule, journeyProgress.phase, plannedMorning?.targetMs],
	);

	const homeStops = activeProfile?.destinationStationIds ?? [];
	const destinationIndex = Math.max(0, homeStops.indexOf(corridorStationId ?? ""));
	// Dated so the pickup can tell a stop he picked from one his morning route left behind.
	const [destinationChosenAtMs, setDestinationChosenAtMs] = useState<number | null>(null);
	const chooseDestination = useCallback(
		(index: number) => {
			setDestinationChosenAtMs(Date.now());
			chooseCorridor(homeStops[index] ?? null);
		},
		[chooseCorridor, homeStops],
	);

	const onwardIndex = Math.max(0, ORIGIN_PRESETS.findIndex((preset) => preset.id === homeForStation(corridorStationId)?.id));
	const chooseOnward = useCallback(
		(index: number) => {
			setDestinationChosenAtMs(Date.now());
			chooseCorridor(stationForHome(ORIGIN_PRESETS[index]?.id ?? null));
		},
		[chooseCorridor],
	);
	const onwardLegs = useMemo<OnwardLeg[]>(() => {
		// The last fix will do for a straight line to a house: it is a distance, not a decision, and
		// underground there is no live one to be had.
		const here = geolocation.position ?? geolocation.lastPosition;
		const measure = (place: { lat: number; lon: number }) => (here ? Math.round(distanceMeters(here, place)) : null);
		if (hasAccessLeg) {
			const place = activeProfile?.finalLocation;
			if (!place || !activeProfile?.finalLabel) {
				return [];
			}
			return [
				{
					id: activeProfile.id,
					label: activeProfile.finalLabel,
					place,
					metersAway: measure(place),
					seconds: activeProfile.egressSeconds || null,
					estimated: true,
				},
			];
		}
		return ORIGIN_PRESETS.map((preset) => ({
			id: preset.id,
			label: preset.onwardLabel ?? preset.label,
			place: { lat: preset.lat, lon: preset.lon },
			metersAway: null,
			seconds: HOME_DRIVE_SECONDS[preset.id] ?? null,
			estimated: true,
		}));
	}, [hasAccessLeg, activeProfile, geolocation.position, geolocation.lastPosition]);

	// Declared above the request because the request needs it: the pinned train is which ride the
	// worker works his progress out along. Stored, because a cold start mid-ride used to lose it.
	const [pinnedId, setPinnedId] = usePinnedTrain(direction);
	const onwardLeg = onwardLegs[onwardIndex] ?? onwardLegs[0] ?? null;
	// Asked for only where it means something, because it is a routing call each time.
	const onwardFrom = cardMode === "offboard" && geolocation.position ? geolocation.position : null;

	const request = useMemo<CommuteRequest>(
		() => ({
			profileId: activeProfileId,
			// The drive only exists on the way to school; the ride home starts on the platform.
			lat: direction === "home" ? undefined : planOrigin?.lat,
			lon: direction === "home" ? undefined : planOrigin?.lon,
			targetMinutes: preferences.targetMinutes ?? undefined,
			earlyToleranceMinutes: preferences.earlyToleranceMinutes ?? undefined,
			lateToleranceMinutes: preferences.lateToleranceMinutes ?? undefined,
				targetDays: scheduledDays,
			egressSeconds: preferences.egressSeconds ?? undefined,
			prepSeconds: preferences.prepSeconds ?? undefined,
			stationBufferSeconds: preferences.stationBufferSeconds ?? undefined,
			// The deck is cut to where he is: nothing behind him until he reaches the station, and
			// nothing ahead of him once he is on a train. The worker also ends the drive from this.
			phase: journeyProgress.phase,
			// Which train he is on, so the worker can work out how far along it he is. Only once he is
			// aboard: before that the pin is a plan, not a place.
			ridePlanId: isBoarded(journeyProgress.phase) ? (pinnedId ?? undefined) : undefined,
			// Which of the two homeward stops is his, so the ride reports that one rather than the
			// Civic Center the train reaches first.
			destinationStopId: homeStops[destinationIndex] ?? undefined,
			// How many "show more trains" taps this view has made today, so a train he already
			// scrolled to does not vanish out from under him on the next refresh.
			extraTrains: extraTrains || undefined,
			onward:
				onwardFrom && onwardLeg
					? {
							from: { lat: onwardFrom.lat, lon: onwardFrom.lon },
							activeId: onwardLeg.id,
							routeSessionId: onwardRouteSessionId,
							destinations: onwardLegs.map((leg) => ({
								id: leg.id,
								to: leg.place,
								fallbackSeconds: leg.seconds ?? 15 * 60,
							})),
						}
					: undefined,
			debug: preferences.debug || undefined,
			simulation: conditionOverrides(preferences.simulation, urlSimulation),
		}),
		[activeProfileId, direction, planOrigin, journeyProgress.phase, pinnedId, homeStops, destinationIndex, extraTrains, onwardFrom, onwardLeg, onwardLegs, onwardRouteSessionId, preferences, urlSimulation, travelDays],
	);

	const { snapshot: fetched, error, loading, fetchedAtMs, offline, refresh } = useSnapshot(request);
	/*
	 * A snapshot belongs to one journey.
	 *
	 * The last one is deliberately kept on screen while the next loads, which is right for a
	 * refresh and wrong for a switch: a 503 on the way to school left the morning's phase sitting
	 * over the afternoon's trains, reporting them all as missed.
	 */
	const snapshot = fetched && (fetched.profile.mode === "return") === (direction === "home") ? fetched : null;
	/*
	 * Private driver planning.
	 *
	 * This is a second snapshot, not a pickup: it names no person, posts no diagnostics, writes
	 * no row and sends no notification. Its only job is to put scheduled Rockridge departures and
	 * David's current drive to the Civic Center curb on the same cards.
	 */
	const pickupPlanningRequest = useMemo<CommuteRequest>(
		() => ({
			profileId: HOME_PROFILE_ID,
			destinationStopId: scheduledReturn ?? undefined,
			lat: pickupPlanningOrigin?.lat ?? commuteOrigin?.lat,
			lon: pickupPlanningOrigin?.lon ?? commuteOrigin?.lon,
			pickupPlanningUntilMinutes: PICKUP_PLANNING_UNTIL_MINUTES,
			phase: "planning",
			simulation: conditionOverrides(preferences.simulation, urlSimulation),
		}),
		[pickupPlanningOrigin, commuteOrigin, preferences.simulation, urlSimulation, scheduledReturn],
	);
	const { snapshot: pickupPlanningSnapshot } = useSnapshot(pickupPlanningRequest, pickupPlanningOpen);
	const pickupPlanningOriginLabel = homeAt(pickupPlanningOrigin ?? commuteOrigin)?.label ?? "your location";

	useEffect(() => {
		let cancelled = false;
		void fetch(`/api/alerts?deviceId=${deviceId}`)
			.then((response) => {
				if (!response.ok) {
					throw new Error("Could not load alert status.");
				}
				return response.json() as Promise<{ subscription: { config: AlertConfig } | null; followed?: boolean }>;
			})
			.then((state) => {
				if (!cancelled) {
					setAlertsEnabled(state.subscription?.config.enabled === true);
					setFollowed(state.followed === true);
					if (state.subscription) {
						setAlertLeads(activeLeadMinutes(state.subscription.config));
						const patch = adoptedAlertDefaults(preferences, state.subscription.config);
						if (Object.keys(patch).length > 0) {
							setPreferences(patch);
						}
					}
				}
			})
			.catch(() => undefined);
		return () => {
			cancelled = true;
		};
	}, [deviceId, preferences.defaultProfileId, preferences.targetMinutes, setPreferences]);

	/*
	 * Tell the cron whether the nudges still apply.
	 *
	 * Once he is past at home the reminders about leaving are pointless, and stepping back to
	 * planning or at home turns them on again — the flag follows the phase both ways rather than
	 * sticking for the rest of the day.
	 */
	useEffect(() => {
		// The reminders belong to the school run. Looking at the ride home must not tell the cron
		// anything about a morning that is over.
		if (!alertsEnabled || !hasAccessLeg) {
			return;
		}
		void fetch("/api/alerts/travel", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ deviceId, traveling: committed }),
		}).catch(() => undefined);
	}, [alertsEnabled, hasAccessLeg, deviceId, committed]);

	// Countdowns must run against the snapshot's clock, not the wall clock, or a simulated
	// morning reports "3835 min from now". Real elapsed time still ticks on top of it.
	const displayNowMs =
		snapshot?.simulated && fetchedAtMs != null ? snapshot.nowMs + Math.max(0, nowMs - fetchedAtMs) : nowMs;

	// The last train to pull out of the platform he is standing on. The journey takes it as
	// evidence that he is on it, which is why the deck keeps departed trains from the station on.
	const lastDepartedPlan = useMemo(() => {
		const gone = (snapshot?.options ?? []).filter((plan) => plan.boarding.predictedMs <= displayNowMs);
		return gone.reduce((latest, plan) => (!latest || plan.boarding.predictedMs > latest.boarding.predictedMs ? plan : latest), null as CommutePlan | null);
	}, [snapshot, displayNowMs]);
	const board = journey.board;
	useEffect(() => {
		if (journeyProgress.phase === "on_platform" && lastDepartedPlan && lastDepartedPlan.boarding.predictedMs >= journeyProgress.sinceMs) {
			applyPin(lastDepartedPlan.id, "departed-train");
			board(lastDepartedPlan.boarding.predictedMs);
		}
	}, [board, journeyProgress.phase, journeyProgress.sinceMs, lastDepartedPlan, applyPin]);

	// Restore the persisted pin so reopening the app shows the same train as the Live Activity.
	useEffect(() => {
		if (!snapshot) {
			return;
		}
		const known = [...snapshot.options, ...snapshot.targetPreview];
		if (selectedId == null || !known.some((plan) => plan.id === selectedId)) {
			// On board, the recommendation is a train he is not on. It is usually absent from the deck
			// entirely; when it is not, taking it moves the card to a train he cannot be riding, which
			// is what happened on 2026-09-01. The one he is sitting on is the last to have left.
			const restored = pinnedId ? known.find((plan) => plan.id === pinnedId)?.id ?? null : null;
			const onDeck = !boarded && snapshot.options.some((plan) => plan.id === snapshot.recommendedId);
			// A missing plan is a transient lack of evidence, not evidence for another train. The old
			// last-item fallback chose the furthest-future departure for two seconds at 06:58, queued
			// stale speech, and did the same after arrival to push a school ETA one hour late.
			const next = restored ?? nextPinnedTrainId(pinnedId, onDeck ? snapshot.recommendedId : null, boarded);
			applyPin(next, restored ? "restore" : "repair");
		}
	}, [snapshot, selectedId, pinnedId, committed, boarded, applyPin]);

	const inPreview = snapshot?.targetPreview.some((plan) => plan.id === selectedId) ?? false;
	// A new alert re-plans the morning, so hand the deck back to the recommendation rather than
	// leaving it on whichever train was swiped to under the old settings. Not once he is aboard:
	// there the recommendation is a train he is not on, and no alert changes which one he took.
	useEffect(() => {
		if (replanSince == null || !snapshot || fetchedAtMs == null || fetchedAtMs < replanSince) {
			return;
		}
		if (boarded) {
			setReplanSince(null);
			return;
		}
		applyPin(snapshot.recommendedId, "replan");
		setReplanSince(null);
	}, [replanSince, snapshot, fetchedAtMs, boarded, applyPin]);

	// The deck follows the selection into whichever group it belongs to.
	const deckPlans = inPreview ? (snapshot?.targetPreview ?? []) : (snapshot?.options ?? []);
	const selectedPlan = deckPlans.find((plan) => plan.id === selectedId) ?? null;
	followPlanContextRef.current = {
		plans: [...(snapshot?.options ?? []), ...(snapshot?.targetPreview ?? [])],
		profileId: activeProfileId, direction, destinationIndex,
		boardingStationName: snapshot?.profile.boardingStation.name ?? null,
	};

	const station = snapshot?.profile.boardingStation ?? null;
	const boardingStation = station ? boardingGeofenceCoordinates(station.id, station) : null;
	// The train he says he is on, which is the one whose timetable may end the ride. Falling back
	// to the selection keeps the fence pointed somewhere before he has pinned anything.
	const pinnedPlan = (pinnedId ? deckPlans.find((plan) => plan.id === pinnedId) : null) ?? selectedPlan;
	// The stop he chose, not the first one listed: the ride home offers two and his train passes
	// through the other on the way, so the wrong one is a fence he crosses mid-journey.
	const arrivalStop = pinnedPlan?.destinations[destinationIndex] ?? pinnedPlan?.destinations[0] ?? null;
	const destinationStation = snapshot?.profile.destinations.find((stop) => stop.id === arrivalStop?.stopId) ?? null;
	const finalPlace = activeProfile?.finalLocation ?? null;
	const finalPlaces = useMemo(
		() => (hasAccessLeg ? (finalPlace ? [finalPlace] : []) : ORIGIN_PRESETS.map((preset) => ({ lat: preset.lat, lon: preset.lon }))),
		[hasAccessLeg, finalPlace],
	);
	// Routed for real, once he is standing there: the drive Google would give him, not the figure
	// the morning was planned against.
	const routedOnward = snapshot?.onwardDrive ?? null;
	const onwardRoadMeters = snapshot?.onwardRoadMeters ?? {};
	const shownLegs = useMemo<OnwardLeg[]>(
		() =>
			onwardLegs.map((leg, index) => ({
				...leg,
				roadMeters: onwardRoadMeters[leg.id] ?? null,
				...(index === onwardIndex && routedOnward
					? { seconds: routedOnward.durationSeconds, estimated: routedOnward.source === "estimate" }
					: {}),
			})),
		[onwardLegs, onwardIndex, onwardRoadMeters, routedOnward],
	);
	const schoolPlace = DESTINATION_ROLES.ROCK.finalLocation;
	const schoolRadius = DESTINATION_ROLES.ROCK.finalRadiusMeters ?? ARRIVAL_METERS;
	const journeySignals = useMemo<JourneySignals>(
		// Nothing to have left on the ride home — the app has no coordinates for school — so the
		// journey counts as under way from the moment it is the one being looked at.
		() => ({ underWay: hasAccessLeg ? geolocation.awayFromHome : true, boarded, nowMs }),
		[hasAccessLeg, geolocation.awayFromHome, boarded, nowMs],
	);
	useEffect(() => {
		const fix = {
			position: geolocation.position,
			fixAtMs: geolocation.fixAtMs,
			boardingStation,
			boardingRadiusMeters: boardingRadiusFor(station?.id),
			destinationStation,
			destinationDueMs: arrivalStop?.predictedMs ?? null,
			// The school run starts at a house, which `awayFromHome` already answers for. The ride
			// home starts at school, which nothing else watches.
			startPlace: hasAccessLeg ? null : schoolPlace,
			startRadiusMeters: schoolRadius,
			startBoundary: hasAccessLeg ? null : COLLEGE_PREP_BOUNDARY,
			finalPlaces,
			finalRadiusMeters: activeProfile?.finalRadiusMeters ?? FINAL_PLACE_METERS,
			finalBoundary: hasAccessLeg ? COLLEGE_PREP_BOUNDARY : null,
		};
		/*
		 * Which answer ended the ride, recorded once per journey.
		 *
		 * The latch keeps no reason, so afterwards a kilometre-wide fix and a nine-metre one look
		 * identical. This is the row that says whether he was seen at the platform or the timetable
		 * spoke for him — and what the fix looked like at that moment either way.
		 */
		const evidence = destinationEvidence(journeySignals, fix);
		if (evidence && !destinationEvidenceRef.current) {
			destinationEvidenceRef.current = evidence;
			report("destination-latched", {
				evidence,
				direction,
				phase: journeyProgress.phase,
				pinnedId,
				arrivalStopId: arrivalStop?.stopId ?? null,
				destinationDueMs: arrivalStop?.predictedMs ?? null,
				arrivalSource: arrivalStop?.source ?? null,
				// The same trip's own stop list. Disagreeing with the due time above is the signal.
				rideNext: snapshot?.ride?.nextStopName ?? null,
				rideStopsToGo: snapshot?.ride?.stopsToGo ?? null,
				rideEtaMs: snapshot?.ride?.destinationEtaMs ?? null,
				accuracyMeters: geolocation.position?.accuracyMeters ?? null,
				fixAgeMs: geolocation.fixAtMs == null ? null : Math.round(nowMs - geolocation.fixAtMs),
				metersToDestination:
					geolocation.position && destinationStation ? Math.round(distanceMeters(geolocation.position, destinationStation)) : null,
			});
		}
		setJourneyState((current) => nextJourneyState(current, journeySignals, fix));
	}, [journeySignals, geolocation.position, geolocation.fixAtMs, station, destinationStation, arrivalStop, finalPlaces, activeProfile, hasAccessLeg, schoolPlace, schoolRadius]);

	/*
	 * The last moment the phone could see him on his own platform.
	 *
	 * Kept as a ref rather than state: it is evidence for a guess, not something the screen is
	 * waiting on, and a fix a second is not worth a render. Cleared with the journey, because
	 * yesterday's platform says nothing about this morning's train.
	 */
	const seenAtPlatformRef = useRef<number | null>(null);
	useEffect(() => {
		const position = geolocation.position;
		if (!position || !station || geolocation.fixAtMs == null) {
			return;
		}
		const sighting = {
			atMs: geolocation.fixAtMs,
			metersFromBoarding: distanceMeters(position, station),
			accuracyMeters: position.accuracyMeters,
		};
		if (isPlatformSighting(sighting)) {
			seenAtPlatformRef.current = sighting.atMs;
		}
	}, [geolocation.position, geolocation.fixAtMs, station]);
	useEffect(() => {
		seenAtPlatformRef.current = null;
	}, [direction, simulatedKey]);
	// Only aboard: before boarding, "the first train to leave after I got here" is not a claim
	// about anything, and the deck is already answering which one to take.
	const [guessedTrainId, setGuessedTrainId] = useState<string | null>(null);
	useEffect(() => {
		setGuessedTrainId((current) => latchBoardedTrain(current, boarded, snapshot?.options ?? [], seenAtPlatformRef.current, Date.now()));
	}, [boarded, snapshot]);
	// A different leg is a different train, and the phase it restores may already be aboard.
	useEffect(() => {
		setGuessedTrainId(null);
	}, [direction, simulatedKey]);

	/*
	 * Every station he passes, once each, with what BART was saying about his train at the time.
	 *
	 * The train is a best guess and that is fine: the point is a record that can be replayed
	 * afterwards — where the phone actually was, how good the fix was, and what the timetable
	 * claimed — rather than a decision. Underground the fix will be poor or absent, and that is
	 * itself worth knowing.
	 */
	const passedRef = useRef<Set<string>>(new Set());
	useEffect(() => {
		const position = geolocation.position;
		if (!position || isBefore(journeyProgress.phase, "at_home")) {
			return;
		}
		const near = Object.values(MAP_STATIONS)
			.map((entry) => ({ entry, meters: distanceMeters(position, entry) }))
			.filter((candidate) => candidate.meters <= STATION_PASS_METERS)
			.sort((a, b) => a.meters - b.meters)[0];
		if (!near || passedRef.current.has(near.entry.id)) {
			return;
		}
		passedRef.current.add(near.entry.id);
		report("station-pass", {
			station: near.entry.id,
			stationName: near.entry.name,
			metersFromStation: Math.round(near.meters),
			lat: position.lat,
			lon: position.lon,
			accuracyMeters: position.accuracyMeters == null ? null : Math.round(position.accuracyMeters),
			speedMetersPerSecond: position.speedMetersPerSecond,
			fixAtMs: geolocation.fixAtMs,
			phase: journeyProgress.phase,
			direction,
			planId: pinnedPlan?.id ?? null,
			tripId: pinnedPlan?.tripId ?? null,
			boardingMs: pinnedPlan?.boarding.predictedMs ?? null,
			boardingSource: pinnedPlan?.boarding.source ?? null,
			profileId: activeProfileId,
			boardingStationName: snapshot?.profile.boardingStation.name ?? null,
			destinationStopId: arrivalStop?.stopId ?? null,
			plan: planDiagnostic(pinnedPlan),
			arrivalStopId: arrivalStop?.stopId ?? null,
			arrivalMs: arrivalStop?.predictedMs ?? null,
			arrivalSource: arrivalStop?.source ?? null,
		});
	}, [geolocation.position, geolocation.fixAtMs, journeyProgress.phase, direction, pinnedPlan, arrivalStop, activeProfileId, snapshot?.profile.boardingStation.name]);
	// A new journey is a new record; yesterday's stations must not silence today's.
	useEffect(() => {
		passedRef.current = new Set();
	}, [direction, simulatedKey]);

	// Simulation can come from the URL or from stored settings, so leaving it has to clear both.
	// No reload: preferences are written by an effect, so navigating away here would leave the
	// simulation on disk and bring it straight back.
	const exitSimulation = () => {
		setPreferences({ simulation: null });
		setUrlSimulation(null);
		window.history.replaceState(null, "", window.location.pathname);
	};

	// The visible card and every outside surface answer from one train. Waiting before pinning made
	// backgrounding preserve a different card from the one still visible in the app.
	const pinTo = useCallback(
		(id: string) => {
			applyPin(id, "swipe");
		},
		[applyPin],
	);
	// The board is a list of rows, so its taps are a different gesture from a swipe across the deck.
	const pinFromBoard = useCallback(
		(id: string) => {
			applyPin(id, "board-tap");
		},
		[applyPin],
	);

	const handleRefresh = () => {
		refresh();
	};

	const recommendedPlan = snapshot?.options.find((plan) => plan.id === snapshot.recommendedId) ?? null;
	// Everything this device does follows the pin as soon as there is one: card, lock screen, voice,
	// wake lock. Push is not decided here — the Worker computes it from its own recommendation — so a
	// swipe can quiet this phone without silencing the reminder for the train he will really catch.
	const activePlan = pinnedId ? (snapshot?.options.find((plan) => plan.id === pinnedId) ?? null) : recommendedPlan;
	const onboardStatus = cardMode === "onboard"
		? observedOnboardStatus({
				plan: activePlan,
				destinationIndex,
				position: geolocation.position,
				fixAtMs: geolocation.fixAtMs,
				nowMs: displayNowMs,
			})
		: null;
	const metersToStation = geolocation.position && station ? Math.round(distanceMeters(geolocation.position, station)) : null;
	const metersFromSchool = geolocation.position && schoolPlace ? Math.round(distanceMeters(geolocation.position, schoolPlace)) : null;

	/*
	 * Where the car is going, which is not the same place all morning.
	 *
	 * Until the drop-off it is the station. After it, the only drive left is home — the card kept
	 * offering Civic Center to a driver who had just come from there. Only the driver is shown it:
	 * on the platform the rider is waiting for a train, and a route to a house would be an answer
	 * to a question he is not asking. `followed` is the app's own record of which phone is which.
	 */
	const driveTarget = useMemo<NavTarget | null>(() => {
		if (!snapshot?.profile.hasAccessLeg || cardMode === "onboard") {
			return null;
		}
		if (isBefore(journeyProgress.phase, "at_station")) {
			return snapshot.profile.boardingStation;
		}
		if (followed) {
			return null;
		}
		const home = homeForStation(snapshot.profile.boardingStation.id, geolocation.position);
		return home ? { id: home.id, name: home.label, lat: home.lat, lon: home.lon, address: home.address } : null;
	}, [snapshot, cardMode, journeyProgress.phase, followed, geolocation.position]);

	/*
	 * Saying he is in the car, which the phone would otherwise have to notice.
	 *
	 * It normally does, and quickly — but only while the page is running, and the page is behind
	 * Waze for exactly the stretch this matters. So the drive is declarable like the platform and
	 * the train, and because the hand is already on the phone the same tap starts the route.
	 */
	const declareStep = useCallback(
		(phase: RiderPhase) => {
			if (supportsActivityPurposes && isBefore(phase, journeyProgress.phase)) {
				setReopenActivity(true);
			}
			journey.declare(phase);
			if (phase === "driving" && driveTarget) {
				// Inside the gesture, or iOS treats it as a pop-up and blocks it.
				window.open(wazeNavigationUrl(driveTarget), "_blank", "noopener");
			}
		},
		[journey, driveTarget, journeyProgress.phase, supportsActivityPurposes],
	);

	/*
	 * `aboard` is the only tap this build can place: it is the same claim the platform pill makes,
	 * and `declarePhase` already refuses to let a standing signal undo it. The other two are
	 * recorded and nothing more — giving behaviour to a button nobody can press yet would be
	 * untested by construction, and the row is what will say whether it ever arrived.
	 */
	nativeActionRef.current = (tapped) => {
		report("native-action", { action: tapped.action, atMs: tapped.atMs, phase: journeyProgress.phase, applied: tapped.action === "aboard" });
		if (tapped.action === "aboard") {
			declareStep("on_train");
		}
	};

	/*
	 * A train confirmed on the wrist.
	 *
	 * Only a train that is still on the deck. The watch may have been showing a card for minutes
	 * before the wrist came up, and pinning a departure the engine has since dropped would put the
	 * Live Activity on a train nobody can name — which is the shape of the 2026-09-01 fault, arriving
	 * by a different route.
	 */
	nativePinRef.current = (pin) => {
		const known = deckPlans.some((plan) => plan.id === pin.planId);
		report("watch-pin", { planId: pin.planId, atMs: pin.atMs, applied: known, phase: journeyProgress.phase });
		if (known) {
			applyPin(pin.planId, "watch-tap");
		}
	};

	/*
	 * The shared meeting.
	 *
	 * Both ends read the same server record, so which of them this phone is depends on the row
	 * rather than on anything decided here. Eben's phone contributes to it through the diagnostics
	 * it already posts; David's drives it through the endpoint.
	 */
	const {
		pickup,
		polledAtMs: pickupPolledAtMs,
		settled: pickupAnswered,
		enter: enterPickup,
		exit: exitPickup,
		cancel: cancelPickup,
		markDriving,
		choose: choosePickupTrain,
		arrive: arriveAtPickup,
		complete: completePickup,
		request: requestPickup,
		standDown: standDownPickup,
		refresh: refreshPickup,
		refreshing: pickupRefreshing,
		error: pickupError,
		saving: pickupSaving,
		ack: ackPickup,
		setPunctuality: setPickupPunctuality,
		markCommunicationShown,
	} = usePickup(
		deviceId,
		geolocation.position,
	);
	const { status: rawFollowStatus, error: followStatusError } = useFollowStatus(isDavid, deviceId);
	// While David is meeting the ride home, the shared pickup record is the sharper witness.
	const followStatus = useMemo(
		() => projectFollowStatus(pickup?.role === "driver" && pickup.status !== "cancelled" && pickup.status !== "expired"
			? reconcileFollowWithPickup(rawFollowStatus, pickup)
			: rawFollowStatus, displayNowMs),
		[rawFollowStatus, pickup, displayNowMs],
	);
	const inferredFollowPosition = useMemo(
		() => followStatus ? estimatedFollowMapPosition(followStatus, displayNowMs) : null,
		[followStatus, displayNowMs],
	);
	const followOverlay = useMemo(() => followJourneyOverlay(followStatus), [followStatus]);
	const riderMapPosition = useMemo(() => followMapPosition(followStatus, displayNowMs), [followStatus, displayNowMs]);
	const inPickupMode = pickup?.role === "driver" && pickup.sharing && pickup.status === "active";
	const pickupModeRequest = useMemo(() => ({ ...pickupPlanningRequest, destinationStopId: pickup?.stationId ?? pickupPlanningRequest.destinationStopId }), [pickupPlanningRequest, pickup?.stationId]);
	const { snapshot: pickupModeSnapshot } = useSnapshot(pickupModeRequest, inPickupMode);
	pickupWantsPositionRef.current = inPickupMode || pickupPlanningOpen;
	// Private planning only owns the screen before the real pickup exists. Entering the shared
	// pickup used to leave the planner open over it, so the state changed but the screen did not.
	useEffect(() => {
		if (inPickupMode) {
			closePickupPlanning();
		}
	}, [inPickupMode, closePickupPlanning]);
	/*
	 * What the lock screen is told, and what it may say back.
	 *
	 * `activePlan` rather than the selection: a swipe is browsing until it settles into a pin, and
	 * browsing the deck should not rewrite the Dynamic Island.
	 *
	 * `locationActive` rides along because this is the message the shell already listens to: the
	 * decision stays here rather than being reproduced by an installed binary that cannot be changed
	 * without a rebuild. The morning window only — `withinRideHomeWindow` runs 11:00 to 22:00 and held
	 * the radio for eleven hours a day when it was in here.
	 */
	const ordinaryNativeJourneyInput = {
		// Told to the shell as `arrived` once he is off the train, which is the only way the web can
		// close the card for good. The app still shows the last leg, and `locationActive` deliberately
		// reads the real phase so the radio can notice the actual destination.
		phase: journeyFinished(journeyProgress.phase) ? ("arrived" as const) : journeyProgress.phase,
		direction,
		reopenActivity: reopenActivity && !pickupPlanningOpen,
		locationActive: pickupPlanningOpen || nativeLocationActive(journeyProgress.phase, inPickupMode, withinWindow),
		mode: cardMode,
		plan: activePlan,
		cardOverride: onboardStatus,
		destinationIndex,
		finalLabel: snapshot?.profile.finalLabel ?? null,
		onward: shownLegs[onwardIndex] ?? shownLegs[0] ?? null,
		boardingLabel: snapshot?.profile.boardingStation ? stationLabel(snapshot.profile.boardingStation.name) : null,
		hasAccessLeg,
		nowMs: displayNowMs,
		timezone: localTimezone,
	};
	const pickupPlanningPlan = pickupPlanningSnapshot?.options.find((plan) => plan.id === pickupPlanningSelectedId)
		?? pickupPlanningSnapshot?.options[0]
		?? null;
	const pickupPlanningArrival = pickupPlanningPlan?.destinations[0] ?? null;
	const pickupPlanningTiming = pickupPlanningSnapshot && pickupPlanningPlan
		? pickupTiming({
				trainArrivalMs: pickupPlanningArrival?.predictedMs ?? null,
				driveSeconds: pickupPlanningSnapshot.drive?.durationSeconds ?? null,
				prepSeconds: PICKUP_PREP_SECONDS,
				driverArrivalMs: null,
				nowMs: displayNowMs,
			})
		: null;
	const pickupPlanningActivityNear = scheduledPickupTravel && pickupActivityWithinHorizon(pickupPlanningTiming?.startByMs ?? null, displayNowMs);
	const pickupPlanningNativeInput = pickupPlanningOpen && pickupPlanningSnapshot && pickupPlanningPlan && pickupPlanningTiming && supportsActivityPurposes
		? {
				phase: pickupPlanningActivityNear ? ("at_home" as const) : ("planning" as const),
				direction: "home" as const,
				activityKey: `${serviceDateKey(displayNowMs, pickupPlanningSnapshot.timezone)}-pickup-planning`,
				reopenActivity,
				locationActive: true,
				presentation: nativePickupPresentation({
					progress: "preparing",
					timing: pickupPlanningTiming,
					station: pickupPlanningArrival?.stopName ?? null,
					trainArrivalMs: pickupPlanningArrival?.predictedMs ?? null,
					nowMs: displayNowMs,
					timezone: pickupPlanningSnapshot.timezone,
				}),
				mode: "leave" as const,
				plan: pickupPlanningPlan,
				destinationIndex: 0,
				finalLabel: null,
				boardingLabel: pickupPlanningSnapshot.profile.boardingStation
					? stationLabel(pickupPlanningSnapshot.profile.boardingStation.name)
					: null,
				hasAccessLeg: false,
				nowMs: displayNowMs,
				timezone: pickupPlanningSnapshot.timezone,
			}
		: null;
	const pickupNativeTrain = pickup?.chosenPlanId
		? pickup.candidates.find((train) => train.planId === pickup.chosenPlanId) ?? pickup.train
		: pickup?.train ?? null;
	/*
	 * Eben's train as a plan the ride-home snapshot can describe.
	 *
	 * Plan ids are `tripId@serviceDate`, so the pickup record and the snapshot identify the same
	 * train. The dedicated snapshot keeps Rockridge, the chosen stop and the line map on screen even
	 * when David's own journey is a different profile.
	 */
	const pickupTrainPlan = useMemo(
		() => (pickupNativeTrain?.planId ? pickupModeSnapshot?.options.find((plan) => plan.id === pickupNativeTrain.planId) ?? null : null),
		[pickupModeSnapshot, pickupNativeTrain],
	);
	const pickupDriveSeconds = pickup?.driverArrivalMs != null && pickup.driverSeenAtMs != null
		? Math.round((pickup.driverArrivalMs - pickup.driverSeenAtMs) / 1000)
		: null;
	const pickupNativeTiming = pickup && pickupNativeTrain
		? pickupTiming({
				trainArrivalMs: pickupNativeTrain.arrivalMs,
				driveSeconds: pickup.driverProgress === "preparing" ? pickupDriveSeconds : null,
				prepSeconds: PICKUP_PREP_SECONDS,
				driverArrivalMs: pickup.driverProgress === "preparing" ? null : pickup.driverArrivalMs,
				nowMs: displayNowMs,
			})
		: null;
	const pickupNativeInput = inPickupMode && pickup && pickupNativeTrain && pickupNativeTiming && supportsActivityPurposes
		? {
				phase: pickup.driverProgress === "picked_up"
					? "arrived" as const
					: pickup.driverProgress === "at_pickup_point"
						? "at_destination" as const
						: pickup.driverProgress === "driving"
							? "driving" as const
							: "at_home" as const,
				direction: "home" as const,
				activityKey: `${pickup.serviceDate.replaceAll("-", "")}-pickup`,
				reopenActivity,
				locationActive: true,
				presentation: nativePickupPresentation({
					progress: pickup.driverProgress,
					timing: pickupNativeTiming,
					station: pickup.stationName,
					trainArrivalMs: pickupNativeTrain.arrivalMs,
					nowMs: displayNowMs,
					timezone: localTimezone,
				}),
				mode: "leave" as const,
				plan: null,
				destinationIndex: 0,
				finalLabel: null,
				boardingLabel: pickup.stationName ? stationLabel(pickup.stationName) : null,
				hasAccessLeg: true,
				nowMs: displayNowMs,
				timezone: localTimezone,
			}
		: null;
	const baseNativeJourneyInput = pickupPlanningNativeInput ?? pickupNativeInput ?? ordinaryNativeJourneyInput;
	const nativeJourneyInput = { ...baseNativeJourneyInput, locationActive: baseNativeJourneyInput.locationActive || mapTest.active || mapLocationWanted };
	const clearActivityReopen = useCallback(() => setReopenActivity(false), []);
	// Silence until the pickup has answered once. Absence of news is not news: a cold start that
	// guessed "no pickup" described the ordinary journey instead, and because that is a different
	// activity key the shell took down the pickup card it was correctly showing and raised it again
	// seconds later — twice on 2026-09-09, at 15:42:52 and 15:46:13.
	useNativeJourney(pickupAnswered ? nativeJourneyInput : null, clearActivityReopen, pickupPlanningOpen || inPickupMode ? null : pinnedId);

	/*
	 * And the wrist, which is the same glance with more room.
	 *
	 * Only the rider's, and only once the pickup has answered, for the same reason the lock screen
	 * waits: a cold start that guessed "no meeting" would describe the ordinary afternoon and then
	 * correct itself seconds later. The driver has his own screen in the car and no pages worth
	 * swiping on a wrist.
	 */
	const riderPickup = pickup?.role === "rider" && pickup.status !== "expired" ? pickup : null;
	useNativeWatch(pickupAnswered && !inPickupMode && !pickupPlanningOpen ? {
		now: nativeJourneyFrom(nativeJourneyInput),
		ride: snapshot?.ride ?? null,
		plans: deckPlans,
		pinnedId,
		recommendedId: snapshot?.recommendedId ?? null,
		destinationIndex,
		mode: cardMode,
		hasAccessLeg,
		pickup: riderPickup
			? {
					kind: riderPickup.status === "completed"
						? "completed"
						: riderPickup.status === "offered"
							? "offered"
							: riderPickup.sharing
								? "active"
								: "paused",
					progress: riderPickup.driverProgress,
					timing: pickupTimingFor(riderPickup, PICKUP_PREP_SECONDS, displayNowMs),
					driverMeters: riderPickup.driverMeters,
					told: riderPickup.pushedStage,
					atShuttleStop: riderPickup.riderAtShuttleStop,
					spot: riderPickup.currentSpot,
					trainArrivalMs: riderPickup.train.arrivalMs,
					stationName: riderPickup.stationName,
					nowMs: displayNowMs,
					timezone: localTimezone,
				}
			: null,
		nowMs: displayNowMs,
		timezone: localTimezone,
	} : null);

	/*
	 * And the same thing to the server, so it can carry on when this page cannot.
	 *
	 * Backgrounding the app suspends these timers, and the lock screen is only ever updated from
	 * here — so without the pin the card freezes at whatever was true when the phone went in the
	 * pocket. The server gets the words, the plan behind them, and the card's own push token.
	 */
	// Pickup activities have their own words and absolute timers; the ordinary commute pin would
	// adopt their token and later overwrite them with rider-journey content.
	useJourneyPin(pickupPlanningOpen || inPickupMode ? null : {
		deviceId,
		journey: nativeJourneyFrom(nativeJourneyInput),
		profileId: snapshot?.profile.id ?? null,
		targetMinutes: preferences.targetMinutes ?? null,
		prepSeconds: preferences.prepSeconds ?? null,
		stationBufferSeconds: preferences.stationBufferSeconds ?? null,
		destinationIndex,
		ridePlanId: pinnedId,
		position: geolocation.position
			? {
					lat: geolocation.position.lat,
					lon: geolocation.position.lon,
					accuracyMeters: geolocation.position.accuracyMeters,
					speedMetersPerSecond: geolocation.position.speedMetersPerSecond,
				}
			: null,
		activity: nativeShell.activity,
	});
	/*
	 * Once he is out of the house he is on his way, which is the only transition on the driver's
	 * side the phone can see for itself.
	 *
	 * Depends on the whole `pickup` object, not on `driverProgress`, so it re-asks on every poll.
	 * Keyed to the primitive it fired exactly once, when `awayFromHome` flipped — and if that post
	 * was lost the deps never changed again, so nothing retried. Eben's screen said his ride was
	 * still getting ready for eighteen minutes of an eighteen-minute drive.
	 */
	useEffect(() => {
		if (inPickupMode && pickup?.driverProgress === "preparing" && geolocation.awayFromHome) {
			markDriving();
		}
	}, [inPickupMode, pickup, geolocation.awayFromHome, markDriving]);

	/*
	 * What Eben's phone tells the server about the pickup.
	 *
	 * Worked out here because this is where the snapshot is: the worker only records what it is
	 * told, which keeps the train reasoning off the request path. The ride-home window, not the
	 * morning school window, keeps evening browsing from raising a pickup nobody asked for.
	 */
	const pickupReport = useMemo<PickupReport | null>(() => {
		if (direction !== "home" || !withinRideHomeWindow || !snapshot || journeyProgress.phase === "arrived") {
			return null;
		}
		const pickupDestinationIndex = destinationIndex;
		const pickupStop = pinnedPlan?.destinations[pickupDestinationIndex] ?? arrivalStop;
		const destinationFix = {
			position: geolocation.position,
			boardingStation,
			boardingRadiusMeters: boardingRadiusFor(station?.id),
			destinationStation,
			destinationDueMs: arrivalStop?.predictedMs ?? null,
			startPlace: null,
			startRadiusMeters: ARRIVAL_METERS,
			finalPlaces: [],
			finalRadiusMeters: ARRIVAL_METERS,
		};
		const arrivalConfidence = observedAtDestination(destinationFix) ||
			((journeyProgress.phase === "on_train" || journeyProgress.phase === "at_destination") && observedLeavingDestination(destinationFix))
			? "observed"
			: journeyProgress.phase === "at_destination"
				? journeyProgress.source === "declared"
					? "declared"
					: "estimated"
				: null;
		return {
			leftSchool: journeyState.leftStart,
			schoolBoundaryVersion: 1,
			metersFromSchool,
			nextStopName: snapshot.ride?.nextStopName ?? null,
			stationId: pickupStop?.stopId ?? null,
			stationName: pickupStop?.stopName ?? null,
			stationChosenAtMs: destinationChosenAtMs,
			pinnedPlanId: pinnedId,
			arrivalConfidence,
			candidates: pickupCandidates(snapshot.options, pickupDestinationIndex, displayNowMs, pinnedId),
		};
	}, [
		direction,
		withinRideHomeWindow,
		snapshot,
		journeyProgress.phase,
		journeyProgress.source,
		journeyState.leftStart,
		metersFromSchool,
		arrivalStop,
		pinnedPlan,
		pinnedId,
		destinationIndex,
		destinationChosenAtMs,
		homeStops,
		pickup,
		displayNowMs,
		geolocation.position,
		station,
		destinationStation,
	]);
	useJourneyDiagnostics(
		// Alerts are the usual reason to keep a record, but the shell cannot subscribe to push and so
		// can never turn them on. Without this it reports audio and nothing else — no position, no
		// phase, and nothing for the console to show.
		alertsEnabled || nativeShell.active,
		{
			// Which bundle wrote the row. Reading a day back otherwise means dating it against the
			// deploy log to work out what he was actually looking at.
			build: BUILD_ID,
			visibility: document.visibilityState,
			online: navigator.onLine,
			profileId: activeProfileId,
			snapshotProfileId: snapshot?.profile.id ?? null,
			direction,
			mode: cardMode,
			alertsEnabled,
			trainAlerts: canSpeakTrain,
			alertLeads,
			targetMinutes: preferences.targetMinutes,
			targetWindow: snapshot?.targetWindow ?? null,
			location: {
				lat: geolocation.position?.lat ?? null,
				lon: geolocation.position?.lon ?? null,
				accuracyMeters: geolocation.position?.accuracyMeters ?? null,
				speedMetersPerSecond: geolocation.position?.speedMetersPerSecond ?? null,
				fixAtMs: geolocation.fixAtMs,
				metersFromHome: geolocation.metersFromHome,
				atHomeLabel: geolocation.atHomeLabel,
				metersToStation,
				// Retained for old clients and diagnostics; the bounded campus now decides departure.
				metersFromSchool,
			},
			// Carried on every row rather than compared: the worker records what it is told, and a
			// heartbeat is as good a moment to be told as a transition.
			pickup: pickupReport,
			// What the server handed back, as opposed to what this phone sent. Without it a poll that
			// never ran and a record that never existed leave exactly the same trace: none.
			pickupPolledAtMs,
			pickupSeen: pickup
				? {
						id: pickup.id,
						role: pickup.role,
						status: pickup.status,
						stage: pickup.stage,
						sharing: pickup.sharing,
						driverProgress: pickup.driverProgress,
						driverArrivalMs: pickup.driverArrivalMs,
						riderSeenAtMs: pickup.riderSeenAtMs,
						driverSeenAtMs: pickup.driverSeenAtMs,
					}
				: null,
			journey: {
				traveling: committed,
				driving,
				atStation: journeyState.atStation,
				forcedTraveling: simulatedAt != null,
				forcedPlatform: false,
				routingFromCar: livePosition != null,
			},
			selected: planDiagnostic(selectedPlan),
			recommended: planDiagnostic(recommendedPlan),
			pinnedId,
			alertPlan: planDiagnostic(activePlan),
			boardingStationName: snapshot?.profile.boardingStation.name ?? null,
			// Where the journey really ends, past the last platform. Only the school run has one.
			finalLabel: snapshot?.profile.finalLabel ?? null,
			// Carried on every row but compared on none: they move with the clock, and as journey state
			// they wrote a diagnostic a second — a third of a day's rows between them.
			seenAtPlatformMs: seenAtPlatformRef.current,
			rideSecondsToNext: snapshot?.ride?.secondsToNext ?? null,
			// Which of the two homeward stops he is counting down to, so anyone meeting him knows
			// which arrival time is the one that matters.
			destinationStopId: activePlan?.destinations[destinationIndex]?.stopId ?? null,
			snapshot: snapshot
				? {
					generatedAtMs: snapshot.generatedAtMs,
					fetchedAtMs,
					feedTimestampMs: snapshot.feed.timestampMs,
					feedAgeSeconds: snapshot.feed.ageSeconds,
					feedAvailable: snapshot.feed.available,
					feedStale: snapshot.feed.stale,
					health: snapshot.health.level,
				}
				: null,
			offline,
			loading,
			error,
		},
		{
			profileId: activeProfileId,
			snapshotProfileId: snapshot?.profile.id ?? null,
			direction,
			mode: cardMode,
			alertsEnabled,
			traveling: committed,
			driving,
			atStation: journeyState.atStation,
			atDestination: journeyState.atDestination,
			// Whether that was a sighting or his train's timetable. The follower's readout says
			// "observed" for one and "inferred" for the other, and cannot tell without this.
			atDestinationEvidence: destinationEvidenceRef.current,
			arrivedAtFinal: journeyState.arrived,
			// The shuttle down the hill, which has no phase of its own.
			leftSchool: journeyState.leftStart,
			phase: journeyProgress.phase,
			phaseSource: journeyProgress.source,
			// What reset would hand back. Null means the evidence is silent, and reset falls to the
			// start of the journey — which is why the same teleport answers differently after dark.
			phaseObserved: journeyProgress.observed,
			// Recorded before it is shown anywhere: "two stops to Rockridge" has to be right on a
			// real ride before it is worth putting on a card. Reaching a stop is the event; the
			// seconds until it are on every row already.
			rideNext: snapshot?.ride?.nextStopName ?? null,
			rideNextId: snapshot?.ride?.nextStopId ?? null,
			rideArriving: snapshot?.ride?.arriving ?? null,
			rideStopsToGo: snapshot?.ride?.stopsToGo ?? null,
			rideDestination: snapshot?.ride?.destinationName ?? null,
			rideEtaMs: snapshot?.ride?.destinationEtaMs ?? null,
			selectedId,
			recommendedId: snapshot?.recommendedId ?? null,
			pinnedId,
			// What the phone's own record says he is on, and whether it agrees with him.
			guessedTrainId,
			guessAgrees: guessedTrainId == null ? null : guessedTrainId === pinnedId,
			planStatus: activePlan?.status ?? null,
			planUrgency: activePlan?.urgency ?? null,
			planFeasible: activePlan?.feasible ?? null,
			feedAvailable: snapshot?.feed.available ?? null,
			feedStale: snapshot?.feed.stale ?? null,
			offline,
		},
	);

	// Sounded at the same moments the push alerts fire, so the two never disagree about when a
	// reminder is due. The words are a clip generated ahead of time: iOS will not speak from a
	// timer, but it will play a decoded buffer from one.
	const spokenAtRef = useRef<Map<string, number>>(new Map());

	/*
	 * The nudge that matters is the one to *enter*, and it is spoken.
	 *
	 * David expects the app to be open beside his computer, so a push alone is the easiest thing
	 * in the world to miss. Said once per pickup, and only while he is not already in it.
	 */
	const spokenPickupRef = useRef<string | null>(null);
	useEffect(() => {
		if (!scheduledPickupTravel || !preferences.soundEnabled || !pickup || pickup.role !== "driver" || pickup.sharing || !pickup.stationId) {
			return;
		}
		if (pickup.status !== "offered" || spokenPickupRef.current === pickup.id) {
			return;
		}
		spokenPickupRef.current = pickup.id;
		speak({ useCase: "pickup", cue: "offer", station: pickup.stationId }, preferences.speechVoice, "headsUp");
	}, [pickup, preferences.soundEnabled, preferences.speechVoice, scheduledPickupTravel]);

	// Both clips fetched and decoded while there is still time to spare, for the same reason the
	// leave reminders are: the first ask for a phrase is the one the server has to generate.
	useEffect(() => {
		if (!preferences.soundEnabled || !pickup || pickup.role !== "driver" || !pickup.stationId) {
			return;
		}
		for (const cue of ["offer", "leave"] as const) {
			void prepareSpeech(deviceId, { useCase: "pickup", cue, station: pickup.stationId }, preferences.speechVoice);
		}
	}, [pickup, preferences.soundEnabled, preferences.speechVoice, deviceId]);

	// One nudge to go, not a ladder: by this point he has been waiting to leave for a while.
	const spokenLeaveRef = useRef<string | null>(null);
	useEffect(() => {
		if (!scheduledPickupTravel || !preferences.soundEnabled || !inPickupMode || !pickup?.stationId || pickup.driverProgress !== "preparing") {
			return;
		}
		const driveSeconds =
			pickup.driverArrivalMs == null || pickup.driverSeenAtMs == null
				? null
				: Math.round((pickup.driverArrivalMs - pickup.driverSeenAtMs) / 1000);
		const { startByMs } = pickupTiming({
			trainArrivalMs: pickup.train.arrivalMs,
			driveSeconds,
			prepSeconds: 5 * 60,
			driverArrivalMs: null,
			nowMs: displayNowMs,
		});
		if (startByMs == null || displayNowMs < startByMs || spokenLeaveRef.current === pickup.id) {
			return;
		}
		spokenLeaveRef.current = pickup.id;
		speak({ useCase: "pickup", cue: "leave", station: pickup.stationId }, preferences.speechVoice, "go");
	}, [inPickupMode, pickup, displayNowMs, preferences.soundEnabled, preferences.speechVoice, scheduledPickupTravel]);

	const boardingStationId = snapshot?.profile.boardingStation.id ?? null;
	// Only the first reminder of the morning is greeted; by the later ones he has been greeted.
	const firstLead = alertLeads.length > 0 ? Math.max(...alertLeads) : null;
	const reminderVariant = useCallback(
		(minutesBefore: number): SpeechVariant | null =>
			boardingStationId == null
				? null
				: {
						useCase: "lead",
						minutesBefore,
						station: boardingStationId,
						...(minutesBefore === firstLead ? { greeting: "morning" as const } : {}),
					},
		[boardingStationId, firstLead],
	);
	const announce = useCallback(
		(minutesBefore: number, category: "leave" | "train") => {
			// The ride home is browsing, not a deadline: he is already on the platform reading the
			// times, and being told what he is looking at is only noise.
			if (!scheduledTravel || !preferences.soundEnabled || direction === "home") {
				return;
			}
			// The timer and an arriving push describe the same reminder, so say it once.
			const key = `${category}-${minutesBefore}`;
			const last = spokenAtRef.current.get(key) ?? 0;
			if (Date.now() - last < 3 * 60_000) {
				return;
			}
			spokenAtRef.current.set(key, Date.now());
			const tier: ChimeTier = minutesBefore <= 1 ? "go" : minutesBefore <= 5 ? "getReady" : "headsUp";
			// The catalogue has words for leaving and none for a train arriving, and saying "leave
			// now" as one pulls in would be a lie. The tone alone still turns his head.
			const variant = category === "leave" ? reminderVariant(minutesBefore) : null;
			if (variant) {
				speak(variant, preferences.speechVoice, tier);
				return;
			}
			// A train pulling in is worth the whole phone: the tone can be missed on a loud platform.
			postHaptic("alert");
			playChime(tier);
		},
		[preferences.soundEnabled, preferences.speechVoice, direction, reminderVariant, scheduledTravel],
	);

	// Fetched and decoded while there is still time to spare: the first time a phrase is ever
	// asked for the server has to generate it, which is not something a deadline can wait on.
	useEffect(() => {
		if (!preferences.soundEnabled || direction === "home" || boardingStationId == null) {
			return;
		}
		for (const minutesBefore of alertLeads) {
			const variant = reminderVariant(minutesBefore);
			if (variant) {
				void prepareSpeech(deviceId, variant, preferences.speechVoice);
			}
		}
	}, [preferences.soundEnabled, preferences.speechVoice, direction, boardingStationId, alertLeads, reminderVariant, deviceId]);

	// A dimmed phone has stopped running the page, so hold the screen over the stretch where a
	// reminder is due — and only there, because the alternative is a flat battery by lunchtime.
	const leaveMomentMs = activePlan?.startByMs ?? activePlan?.leaveByMs ?? null;
	const firstReminderMs = leaveMomentMs == null || alertLeads.length === 0 ? null : leaveMomentMs - Math.max(...alertLeads) * 60_000;
	// The stretch worth spending battery on: from a little before the first reminder to the
	// moment he has to be walking out.
	const reminderWindow =
		firstReminderMs != null && leaveMomentMs != null && displayNowMs >= firstReminderMs - WAKE_LEAD_MS && displayNowMs <= leaveMomentMs;
	useWakeLock(reminderWindow);
	// Spoken about the train on the card. A push from the Worker may still be about the
	// recommendation, and the two disagreeing is the point rather than a bug.
	useEffect(() => {
		if (!scheduledTravel || !activePlan) {
			cancelPendingSpeech();
			return;
		}
		const ready = dueSpokenReminder({
			nowMs: displayNowMs,
			leaveByMs: activePlan.startByMs ?? activePlan.leaveByMs,
			boardingMs: activePlan.boarding.predictedMs,
			leadMinutes: alertLeads,
			trainAlerts: canSpeakTrain,
			// The phase decides whether leaving is still a thing worth being told about.
			traveling: !canSpeakLeave,
		});
		if (ready) {
			announce(ready.minutesBefore, ready.category);
		} else {
			// A transient recommendation briefly moved today's 15-minute reminder to "now". Audio
			// resumed 36 seconds later, after the plan had reverted, and played it beside the real
			// five-minute reminder. The current plan gets the final word before queued audio starts.
			cancelPendingSpeech();
		}
	}, [displayNowMs, activePlan, alertLeads, canSpeakLeave, canSpeakTrain, announce, scheduledTravel]);

	// A push landing while the app is open speaks too, which is the case a timer cannot cover
	// after iOS has suspended it.
	useEffect(() => {
		if (!("serviceWorker" in navigator)) {
			return;
		}
		const onMessage = (event: MessageEvent) => {
			const data = event.data as { type?: string; kind?: string; minutesBefore?: number | null } | null;
			if (scheduledTravel && data?.type === "alert-due") {
				noteAlertDiagnostics({ lastPush: { minutesBefore: data.minutesBefore ?? null, atMs: Date.now() } });
				if (typeof data.minutesBefore === "number") {
					announce(data.minutesBefore, data.kind?.startsWith("train-") ? "train" : "leave");
				}
			}
		};
		navigator.serviceWorker.addEventListener("message", onMessage);
		return () => navigator.serviceWorker.removeEventListener("message", onMessage);
	}, [announce, scheduledTravel]);

	// Audio is gesture-gated on iOS, so take every touch as a chance to unlock it. Unlike speech,
	// one unlock is enough: the context stays running and a timer can play through it.
	useEffect(() => {
		const unlock = () => {
			noteGesture();
			unlockAudio();
		};
		window.addEventListener("pointerdown", unlock);
		return () => window.removeEventListener("pointerdown", unlock);
	}, []);

	// Watch for the footer drifting off the bottom edge. Sampling continuously is the only way
	// to catch a fault that heals before anyone can open the panel and look at it.
	useEffect(() => {
		if (!preferences.debug) {
			return;
		}
		const sample = () => {
			const footer = document.querySelector(".statusbar");
			if (footer) {
				noteFooterGap(Math.round(window.innerHeight - footer.getBoundingClientRect().bottom));
			}
		};
		const timer = window.setInterval(sample, 1000);
		window.addEventListener("resize", sample);
		window.visualViewport?.addEventListener("resize", sample);
		window.visualViewport?.addEventListener("scroll", sample);
		return () => {
			window.clearInterval(timer);
			window.removeEventListener("resize", sample);
			window.visualViewport?.removeEventListener("resize", sample);
			window.visualViewport?.removeEventListener("scroll", sample);
		};
	}, [preferences.debug]);

	/*
	 * The Settings page governs the cron too, so the server's copy follows the device's.
	 *
	 * Sound decides only the in-app spoken reminder. Notification sound belongs to iOS Settings.
	 * The two timings decide the leave time itself — set them here and the card moved while the push
	 * did not, so a reminder fired off a schedule nobody had been shown.
	 */
	const syncedSettingsRef = useRef<string | null>(null);
	useEffect(() => {
		const settings = {
			soundEnabled: preferences.soundEnabled,
			prepSeconds: preferences.prepSeconds,
			stationBufferSeconds: preferences.stationBufferSeconds,
		};
		const signature = JSON.stringify(settings);
		if (syncedSettingsRef.current === null) {
			syncedSettingsRef.current = signature;
			return;
		}
		if (syncedSettingsRef.current === signature || !alertsEnabled) {
			return;
		}
		syncedSettingsRef.current = signature;
		void fetch("/api/alerts/sound", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ deviceId, ...settings }),
		}).catch(() => undefined);
	}, [alertsEnabled, deviceId, preferences.soundEnabled, preferences.prepSeconds, preferences.stationBufferSeconds]);

	return (
		<div className="app" data-action-band={ACTION_BAND} style={{ "--ui-scale": preferences.textScale } as CSSProperties}>
			<div className="app__frame">
				<main className="app__scroll" ref={scrollRef}>
				{locationOverride.error ? <p role="alert">{locationOverride.error}</p> : null}
				{travelSchedule.error ? <p role="alert">{travelSchedule.error}</p> : null}
				{snapshot ? (
					<SimulationBanner snapshot={snapshot} locationLabel={simulatedLocationLabel(simulatedAt)} onExit={exitSimulation} />
				) : null}
				{snapshot && !inPickupMode && (!pickupPlanningOpen || pickupPlanningSnapshot) ? (
					<>
						<RoutePicker snapshot={pickupPlanningOpen && pickupPlanningSnapshot ? pickupPlanningSnapshot : snapshot} pickupOrigin={pickupPlanningOpen ? pickupPlanningOriginLabel : undefined} direction={direction} onDirectionChange={chooseDirection} onOpenAlerts={() => setAlertsOpen(true)} />
						{isDavid && direction === "home" && !pickupPlanningOpen ? <PickupPlanningButton onClick={openPickupPlanning} /> : null}
					</>
				) : null}

				{pickupPlanningOpen && pickupPlanningSnapshot ? (
					<PickupPlanning
						snapshot={pickupPlanningSnapshot}
						nowMs={displayNowMs}
						originLabel={pickupPlanningOriginLabel}
						cutoffMs={pickupPlanningCutoffMs(displayNowMs, pickupPlanningSnapshot.timezone)}
						onShowNotices={() => setNoticesOpen(true)}
						riderStatus={<RiderMonitor status={followStatus} refreshError={followStatusError} timezone={pickupPlanningSnapshot.timezone} nowMs={displayNowMs} />}
						maps={<PickupMaps
							deviceId={deviceId}
							stationId={pickupPlanningSnapshot.profile.destinations[0]?.id ?? null}
							nowMs={displayNowMs}
							colorMode={preferences.colorMode}
							preview={{ position: planningMapFix(geolocation.position && geolocation.fixAtMs != null ? { ...geolocation.position, fixAtMs: geolocation.fixAtMs } : null, followStatus?.driverLastKnownPosition, displayNowMs), rider: { name: "Eben", position: riderMapPosition, approximate: inferredFollowPosition != null, signalPosition: inferredFollowPosition ? followStatus?.lastKnownPosition ?? null : null } }}
							journeyOverlay={followOverlay}
						/>}
						selectedId={pickupPlanningSelectedId}
						onSelect={setPickupPlanningSelectedId}
						diagnostics={
							preferences.debug ? (
								<DebugPanel
									snapshot={pickupPlanningSnapshot}
									nowMs={displayNowMs}
									leads={alertLeads}
									leaveByMs={null}
									soundEnabled={preferences.soundEnabled}
									deviceId={deviceId}
									location={{
										watching: pickupPlanningOpen,
										pending: geolocation.pending,
										error: geolocation.error,
										accuracyMeters: geolocation.position?.accuracyMeters ?? null,
										fixAtMs: geolocation.fixAtMs,
										speedMetersPerSecond: geolocation.position?.speedMetersPerSecond ?? null,
										traveling: false,
										driving: false,
										mode: "onboard",
										metersFromHome: geolocation.metersFromHome,
										atHomeLabel: geolocation.atHomeLabel,
										forced: simulatedAt != null,
										routingFromCar: false,
									}}
								/>
							) : null
						}
						onClose={closePickupPlanning}
					/>
				) : null}

				{inPickupMode && pickup ? (
					<>
						{pickupModeSnapshot ? (
							<RoutePicker snapshot={pickupModeSnapshot} direction="home" hideDirection onDirectionChange={chooseDirection} onOpenAlerts={() => setAlertsOpen(true)} />
						) : null}
						<PickupCard
							pickup={pickup}
							timezone={pickupModeSnapshot?.timezone ?? snapshot?.timezone ?? localTimezone}
							nowMs={displayNowMs}
							onChoose={choosePickupTrain}
						/>
						<RiderMonitor
							status={followStatus}
							refreshError={followStatusError}
							timezone={pickupModeSnapshot?.timezone ?? snapshot?.timezone ?? localTimezone}
							nowMs={displayNowMs}
							calledOff={pickupCalledOff(pickup.status, pickup.stoodDownAtMs)}
						/>
						<PickupCommunication pickup={pickup} saving={pickupSaving} error={pickupError} onAck={ackPickup} onPunctuality={setPickupPunctuality} />
						{pickup.role === "driver" && pickup.status === "active" && pickup.sharing ? (
							<PickupMaps
								key={pickup.id}
								deviceId={deviceId}
								stationId={pickup.stationId}
								nowMs={displayNowMs}
								colorMode={preferences.colorMode}
								riderOverride={followStatus ? { position: riderMapPosition, approximate: inferredFollowPosition != null, signalPosition: followStatus.lastKnownPosition ?? null } : null}
								journeyOverlay={followOverlay}
							/>
						) : null}
						<div className="app__detail">
							{pickupModeSnapshot ? (
								<>
									<ServiceHealthCard snapshot={pickupModeSnapshot} onShowNotices={() => setNoticesOpen(true)} />
									{pickupTrainPlan ? (
										<UpstreamTrace
											plan={pickupTrainPlan}
											snapshot={pickupModeSnapshot}
											nowMs={displayNowMs}
											onboard
											destinationStopId={pickup.stationId}
										/>
									) : null}
								</>
							) : null}
							<WhatsNew />
						</div>
					</>
				) : null}

				{error && !snapshot ? (
					<section className="panel panel--bad">
						<header className="panel__head">
							<h2>Cannot reach GoTime</h2>
						</header>
						<p className="panel__detail">{error}</p>
						<button type="button" className="picker__reset" onClick={handleRefresh}>
							Try again
						</button>
					</section>
				) : null}

				{!snapshot && loading ? <div className="skeleton">Working out your commute…</div> : null}

				{snapshot && snapshot.options.length === 0 ? (
					<section className="panel panel--warn">
						<header className="panel__head">
							<h2>No trains</h2>
						</header>
						<p className="panel__detail">
							Nothing runs {stationLabel(snapshot.profile.boardingStation.name)} to{" "}
							{stationLabel(snapshot.profile.destinations[0]?.name ?? "")} in the next couple of hours.
						</p>
						{!inPreview && snapshot.moreTrainsAvailable ? (
							<button type="button" className="picker__reset" onClick={requestMoreTrains} disabled={loading}>
								{loading ? "Looking further out…" : "Look further out"}
							</button>
						) : null}
					</section>
				) : null}

				{snapshot && snapshot.options.length > 0 && !inPickupMode && !pickupPlanningOpen ? (
					<>
						{cardMode === "offboard" || cardMode === "done" ? (
							<OnwardCard
								legs={cardMode === "done" ? [] : shownLegs}
								chosen={onwardIndex}
								homeward={!hasAccessLeg}
								saying={snapshot.saying}
								onChoose={chooseOnward}
								pickup={null}
								onRequestPickup={requestPickup}
								onStandDownPickup={standDownPickup}
								nowMs={displayNowMs}
							/>
						) : (
							<PlanDeck
								snapshot={snapshot}
								plans={deckPlans}
								preview={inPreview}
								mode={cardMode}
								nowMs={displayNowMs}
								selectedId={selectedId}
								onSelect={pinTo}
								destinationIndex={destinationIndex}
								onChooseDestination={chooseDestination}
								driveTarget={driveTarget}
								pinnedId={pinnedId}
								guessedId={guessedTrainId}
								pickup={null}
								onRequestPickup={requestPickup}
								onStandDownPickup={standDownPickup}
								cardOverride={onboardStatus}
								moreAvailable={!inPreview && snapshot.moreTrainsAvailable}
								onLoadMore={requestMoreTrains}
								loadingMore={loading}
							/>
						)}
						{pickup?.role === "rider" ? (
							<PickupBlock pickup={pickup} nowMs={displayNowMs} onRequest={requestPickup} onStandDown={standDownPickup} onRefresh={refreshPickup} refreshing={pickupRefreshing} error={pickupError} onCommunicationShown={markCommunicationShown} />
						) : null}
						{pickup?.role === "rider" && pickup.status === "active" && pickup.sharing ? (
							<PickupMaps key={pickup.id} deviceId={deviceId} stationId={pickup.stationId} nowMs={displayNowMs} colorMode={preferences.colorMode} initialView="pickup" />
						) : null}
						<RiderMonitor
							status={followStatus}
							refreshError={followStatusError}
							timezone={snapshot.timezone}
							nowMs={displayNowMs}
							calledOff={pickup?.role === "driver" ? pickupCalledOff(pickup.status, pickup.stoodDownAtMs) : null}
						/>
						{isDavid && followStatus && showMorningFollowMap(followStatus, journeyProgress.phase, direction, station?.id ?? null) ? (
							<PickupMaps
								deviceId={deviceId}
								stationId={null}
								nowMs={displayNowMs}
								colorMode={preferences.colorMode}
								preview={{
									position: null,
									rider: {
										name: "Eben",
										position: riderMapPosition,
										approximate: inferredFollowPosition != null,
										signalPosition: inferredFollowPosition ? followStatus.lastKnownPosition ?? null : null,
									},
								}}
								followOnly
								journeyOverlay={followOverlay}
							/>
						) : null}
						{pickup?.role === "driver" && <PickupCommunication pickup={pickup} saving={pickupSaving} error={pickupError} onAck={ackPickup} onPunctuality={setPickupPunctuality} />}
						<div className="app__detail">
							<SupportingMetrics plan={selectedPlan} snapshot={snapshot} />

							{snapshot.notes.length > 0 ? (
								<ul className="notes">
									{snapshot.notes.map((note) => (
										<li key={note}>{note}</li>
									))}
								</ul>
							) : null}

							<ServiceHealthCard snapshot={snapshot} onShowNotices={() => setNoticesOpen(true)} />
							<TrainBoard
								snapshot={snapshot}
								selectedId={selectedId}
								mode={cardMode}
								nowMs={displayNowMs}
								destinationIndex={destinationIndex}
								onSelect={pinFromBoard}
								moreAvailable={snapshot.moreTrainsAvailable}
								onLoadMore={requestMoreTrains}
								loadingMore={loading}
							/>
							<UpstreamTrace
								plan={selectedPlan}
								snapshot={snapshot}
								nowMs={displayNowMs}
								onboard={cardMode === "onboard"}
								destinationStopId={selectedPlan?.destinations[destinationIndex]?.stopId ?? null}
							/>

							<WhatsNew />

							{preferences.debug ? (
								<DebugPanel
									snapshot={snapshot}
									nowMs={displayNowMs}
									leads={alertLeads}
									leaveByMs={activePlan?.leaveByMs ?? null}
									soundEnabled={preferences.soundEnabled}
									deviceId={deviceId}
									location={{
										watching: alertsEnabled,
										pending: geolocation.pending,
										error: geolocation.error,
										accuracyMeters: geolocation.position?.accuracyMeters ?? null,
										fixAtMs: geolocation.fixAtMs,
										speedMetersPerSecond: geolocation.position?.speedMetersPerSecond ?? null,
										traveling: committed,
										driving,
										mode: cardMode,
										metersFromHome: geolocation.metersFromHome,
										atHomeLabel: geolocation.atHomeLabel,
										forced: simulatedAt != null,
										routingFromCar: livePosition != null,
									}}
								/>
							) : null}
						</div>
					</>
				) : null}
				</main>

				<div className="app__topveil" aria-hidden="true" />

				<ScrollTopButton targetRef={scrollRef} className="app__toTop" />

				{/* One slot, and the pickup wins it: a live shared commitment beats a step of his own
				    journey, which in pickup mode he is not having. */}
				{pickup && pickup.role === "driver" && (pickup.status === "offered" || pickup.status === "active") ? (
					<PickupButton
						pickup={pickup}
						nowMs={displayNowMs}
						onEnter={enterPickup}
						onExit={exitPickup}
						onCancel={cancelPickup}
						onDriving={markDriving}
						onArrive={arriveAtPickup}
						onComplete={completePickup}
					/>
				) : SHOW_NEXT_STEP_BUTTON ? (
					<NextStepButton phase={journeyProgress.phase} phases={journeyPhases} onDeclare={declareStep} />
				) : null}
			</div>

			{snapshot && !inPickupMode && !pickupPlanningOpen ? (
				<ProgressLine
					progress={journeyProgress}
					phases={journeyPhases}
					hasAccessLeg={hasAccessLeg}
					onDeclare={declareStep}
					onReset={journey.reset}
				/>
			) : null}

			<StatusBar
				snapshot={snapshot}
				fetchedAtMs={fetchedAtMs}
				nowMs={nowMs}
				loading={loading}
				offline={offline}
				alertsEnabled={alertsEnabled}
				native={{ active: nativeShell.active, stale: nativeShell.stale }}
				onRefresh={handleRefresh}
				onOpenSettings={() => setSettingsOpen(true)}
				onOpenAlerts={() => setAlertsOpen(true)}
			/>

			{alertsOpen ? (
				<AlertsSheet
					travelSchedule={travelSchedule}
					snapshot={snapshot}
					deviceId={deviceId}
					preferredProfileId={preferences.defaultProfileId}
					preferredTargetMinutes={preferences.targetMinutes}
					soundEnabled={preferences.soundEnabled}
					prepSeconds={preferences.prepSeconds}
					stationBufferSeconds={preferences.stationBufferSeconds}
					earlyToleranceMinutes={preferences.earlyToleranceMinutes}
					lateToleranceMinutes={preferences.lateToleranceMinutes}
					egressSeconds={preferences.egressSeconds}
					push={nativeShell.push}
					onTimingChange={setPreferences}
					onDefaultsChange={(defaults) => {
						setAlertLeads(defaults.leads);
						setPreferences({
							targetMinutes: defaults.targetMinutes,
							targetDays: defaults.days,
							earlyToleranceMinutes: defaults.earlyToleranceMinutes,
							lateToleranceMinutes: defaults.lateToleranceMinutes,
							egressSeconds: defaults.egressSeconds,
							prepSeconds: defaults.prepSeconds,
							stationBufferSeconds: defaults.stationBufferSeconds,
						});
					}}
					onEnabledChange={setAlertsEnabled}
					onSaved={() => setReplanSince(Date.now())}
					onClose={() => setAlertsOpen(false)}
				/>
			) : null}

			{settingsOpen ? (
				<SettingsSheet
					mapTest={mapTest}
					snapshot={snapshot}
					preferences={preferences}
					deviceId={deviceId}
					destinationStopId={selectedPlan?.destinations[destinationIndex]?.stopId ?? null}
					position={geolocation.position}
					nativeJourney={nativeJourneyFrom(nativeJourneyInput)}
					nowMs={displayNowMs}
					onChange={setPreferences}
					onOpenGuide={() => setGuideOpen(true)}
					onClose={() => setSettingsOpen(false)}
				/>
			) : null}

			{guideOpen ? <GuideSheet onClose={() => setGuideOpen(false)} /> : null}

			{noticesOpen && snapshot ? (
				<AlertSheet
					alerts={snapshot.alerts.filter((alert) => alert.affectsThisTrip || alert.suppressed)}
					onClose={() => setNoticesOpen(false)}
				/>
			) : null}
		</div>
	);
}

function profileFromUrl(): string | null {
	return new URLSearchParams(window.location.search).get("profile");
}

function simulationFromUrl(): SimulationOverrides | null {
	const params = new URLSearchParams(window.location.search);
	const overrides: SimulationOverrides = {};

	const at = params.get("at");
	const parsedAt = at ? Date.parse(at) : Number.NaN;
	if (Number.isFinite(parsedAt)) {
		overrides.nowMs = parsedAt;
	}

	const lat = Number(params.get("lat"));
	const lon = Number(params.get("lon"));
	if (Number.isFinite(lat) && Number.isFinite(lon) && params.has("lat")) {
		overrides.position = { lat, lon };
	}

	const numeric: Array<[string, keyof SimulationOverrides]> = [
		["drive", "driveSeconds"],
		["delay", "delaySeconds"],
		["buffer", "stationBufferSeconds"],
	];
	for (const [param, key] of numeric) {
		const value = Number(params.get(param));
		if (params.has(param) && Number.isFinite(value)) {
			(overrides[key] as number) = value;
		}
	}

	if (params.get("norealtime") === "1") {
		overrides.disableRealtime = true;
	}
	if (params.get("norouting") === "1") {
		overrides.disableRouting = true;
	}

	return Object.keys(overrides).length > 0 ? overrides : null;
}

function mergeSimulation(stored: SimulationOverrides | null, fromUrl: SimulationOverrides | null): SimulationOverrides | undefined {
	if (!stored && !fromUrl) {
		return undefined;
	}
	return { ...stored, ...fromUrl };
}

/**
 * Everything the server should pretend *except* where he is.
 *
 * The location override stands in for the phone's fix, and the phase decides what the plan does
 * with it. Sent on as a simulated position it would outrank that and route from wherever the
 * override sits, even while planning — which is the opposite of the point.
 */
function conditionOverrides(stored: SimulationOverrides | null, fromUrl: SimulationOverrides | null): SimulationOverrides | undefined {
	const merged = mergeSimulation(stored, fromUrl);
	if (!merged) {
		return undefined;
	}
	const { position, ...rest } = merged;
	void position;
	return Object.keys(rest).length > 0 ? rest : undefined;
}
